import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, Search, Trash2, Link as LinkIcon, Copy } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { uploadMediaFile } from './uploader';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatSize = (b) => {
  if (!b) return '—';
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

export const MediaLibrary = ({ adminToken }) => {
  const [media, setMedia] = useState([]);
  const [search, setSearch] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [externalUrl, setExternalUrl] = useState('');
  const fileRef = useRef(null);

  const fetchMedia = useCallback(async (q = '') => {
    try {
      const res = await axios.get(`${API_URL}/admin/media`, { params: { admin_token: adminToken, search: q || undefined } });
      setMedia(res.data.media || []);
    } catch {
      toast.error('Failed to load media');
    }
  }, [adminToken]);

  useEffect(() => { fetchMedia(); }, [fetchMedia]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        await uploadMediaFile(file, adminToken, setProgress);
      }
      toast.success(`${files.length} image${files.length > 1 ? 's' : ''} uploaded to the master gallery`);
      fetchMedia(search);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      setProgress(0);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const registerExternal = async () => {
    if (!externalUrl.startsWith('http')) {
      toast.error('Enter a valid image URL');
      return;
    }
    try {
      await axios.post(`${API_URL}/admin/media/register`, { url: externalUrl }, { params: { admin_token: adminToken } });
      toast.success('Image added to library');
      setExternalUrl('');
      fetchMedia(search);
    } catch {
      toast.error('Failed to add image');
    }
  };

  const remove = async (m) => {
    if (!window.confirm('Remove this image from the library? Pages already using it keep working.')) return;
    try {
      await axios.delete(`${API_URL}/admin/media/${m.id}`, { params: { admin_token: adminToken } });
      toast.success('Removed from library');
      fetchMedia(search);
    } catch {
      toast.error('Failed to remove');
    }
  };

  const copyUrl = (m) => {
    const url = m.url.startsWith('http') ? m.url : `${process.env.REACT_APP_BACKEND_URL}${m.url}`;
    navigator.clipboard.writeText(url);
    toast.success('Image URL copied');
  };

  return (
    <div className="bg-white rounded-xl border border-stone-200 overflow-hidden" data-testid="media-library">
      <div className="px-6 py-4 border-b border-stone-200 bg-stone-50 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-medium text-stone-900">Media Library — Master Gallery</h3>
          <p className="text-xs text-stone-500 mt-0.5">Every image on your site lives here. Upload once, reuse anywhere via the Library button on image fields.</p>
        </div>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={handleUpload} data-testid="media-file-input" />
          <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-stone-900 hover:bg-stone-800" data-testid="media-upload-btn">
            <Upload className="w-4 h-4 mr-1" /> {uploading ? `Uploading ${progress}%` : 'Upload Images'}
          </Button>
        </div>
      </div>

      <div className="px-6 py-4 border-b border-stone-100 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input className="pl-9" placeholder="Search images…" value={search} onChange={e => { setSearch(e.target.value); fetchMedia(e.target.value); }} data-testid="media-search-input" />
        </div>
        <div className="flex items-center gap-2 flex-1 min-w-[260px]">
          <Input placeholder="Or paste an external image URL…" value={externalUrl} onChange={e => setExternalUrl(e.target.value)} data-testid="media-external-url-input" />
          <Button variant="outline" size="sm" className="h-10 shrink-0" onClick={registerExternal} data-testid="media-register-url-btn">
            <LinkIcon className="w-4 h-4 mr-1" /> Add URL
          </Button>
        </div>
      </div>

      {media.length === 0 ? (
        <p className="text-sm text-stone-400 text-center py-14">No images yet. Upload your first image or add one by URL.</p>
      ) : (
        <div className="p-5 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-4">
          {media.map(m => (
            <div key={m.id} className="border border-stone-200 rounded-xl overflow-hidden group" data-testid={`media-item-${m.id}`}>
              <div className="aspect-[4/3] bg-stone-100 overflow-hidden">
                <img src={m.url} alt={m.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
              </div>
              <div className="px-3 py-2.5">
                <p className="text-xs font-medium text-stone-800 truncate" title={m.name}>{m.name}</p>
                <div className="flex items-center justify-between mt-1.5">
                  <span className="text-[10px] text-stone-400">{m.source === 'upload' ? formatSize(m.size) : 'external'}</span>
                  <div className="flex items-center gap-0.5">
                    <button className="p-1.5 text-stone-400 hover:text-stone-800" title="Copy URL" onClick={() => copyUrl(m)} data-testid={`media-copy-${m.id}`}>
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                    <button className="p-1.5 text-stone-400 hover:text-red-500" title="Remove" onClick={() => remove(m)} data-testid={`media-delete-${m.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
